import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { followOnWorksHref, isKnownStatus, primaryStatePill, statusEntry, type PillVariant, type StateFacts, type StatePillSpec, type StatusDomain } from "../lib/statusRegistry";

// A status pill (Session AAV, Loop 4): a small coloured dot beside the status word, per the
// locked design direction -- colour reinforces the word and is never the only signal (the PPM
// payment pills established the pattern).
//
// TONE (Session AQT, Phase 1 design system foundation): this component's own locked dot+word idiom
// plus a tinted background matching .ele-pill's visual language, computed from ONE per-tone table.
//
// SESSION AUJ (design system Session 2), spec section 3.3:
//  - INK AND TINT ARE CUSTOM PROPERTIES now (--ele-pill-ink-<tone>, --ele-pill-tint-<tone> in
//    src/styles/ele-theme.css), not hex literals here, so the app-UI contrast lint
//    (src/styles/appContrast.test.ts) can READ every value it measures. That lint found three
//    defects in the literals this file used to carry: purple ink #7d21c7 on the dark theme
//    (2.64:1), red ink #c0392b on the dark theme (3.48:1), and blue ink on the light theme's 10%
//    tint (4.47:1). Each is fixed in the tokens, not here.
//  - VARIANTS: `tint` (the look it always had), `solid` (a flat brand fill, ink by the CTA rule:
//    dark on green / blue / loler / amber, white on purple and on the fixed red #c0392b; no dot),
//    `outline` (transparent, a 1px flat-hex border; "an office action is needed").
//  - REGISTRY FORM: <StatusPill domain="job" status={job.status} /> reads src/lib/statusRegistry.ts,
//    the one canonical status-to-tone map. An unknown status shows its humanised word on grey and
//    carries data-status-unknown; it is never blank.
//  - toneTextClass: for a coloured WORD with no pill (an SLA percentage).
export type PillTone = "green" | "blue" | "loler" | "amber" | "red" | "purple" | "grey";
export const PILL_TONES = ["green", "blue", "loler", "amber", "red", "purple", "grey"] as const satisfies readonly PillTone[];
export const PILL_VARIANTS = ["tint", "solid", "outline"] as const satisfies readonly PillVariant[];

const TONE: Record<PillTone, { dot: string; ink: string; tint: string; border: string }> = {
  green: { dot: "#63C41F", ink: "var(--ele-pill-ink-green)", tint: "var(--ele-pill-tint-green)", border: "rgba(99, 196, 31, 0.3)" },
  blue: { dot: "#00C4FF", ink: "var(--ele-pill-ink-blue)", tint: "var(--ele-pill-tint-blue)", border: "rgba(0, 196, 255, 0.3)" },
  // The document/compliance/job-type blue (distinct from app blue), per the canonical job-type scheme.
  loler: { dot: "#2CB9F3", ink: "var(--ele-pill-ink-loler)", tint: "var(--ele-pill-tint-loler)", border: "rgba(44, 185, 243, 0.3)" },
  amber: { dot: "#b8860b", ink: "var(--ele-pill-ink-amber)", tint: "var(--ele-pill-tint-amber)", border: "rgba(184, 134, 11, 0.3)" },
  red: { dot: "#c0392b", ink: "var(--ele-pill-ink-red)", tint: "var(--ele-pill-tint-red)", border: "rgba(192, 57, 43, 0.3)" },
  purple: { dot: "#7d21c7", ink: "var(--ele-pill-ink-purple)", tint: "var(--ele-pill-tint-purple)", border: "rgba(125, 33, 199, 0.3)" },
  grey: { dot: "#9ca3af", ink: "var(--ele-pill-ink-grey)", tint: "var(--ele-pill-tint-grey)", border: "rgba(255, 255, 255, 0.2)" },
};

// The SOLID variant: the flat brand hex as the fill and the ink the CTA rule gives it (white on a
// bright green or blue fails AA at ~2.2:1; docs/reference/brand.md). A solid red fill is ALWAYS the
// fixed #c0392b and never the themed --ele-danger (#FF5A5A on dark: white on it is 3.06:1, defect D4).
// Grey fills with the interactive-outline token and reads the body ink, so it follows the theme.
// Exported so the contrast lint measures exactly these pairs.
export const PILL_SOLID: Record<PillTone, { fill: string; ink: string }> = {
  green: { fill: "#63C41F", ink: "#050816" },
  blue: { fill: "#00C4FF", ink: "#050816" },
  loler: { fill: "#2CB9F3", ink: "#050816" },
  amber: { fill: "#b8860b", ink: "#050816" },
  red: { fill: "#c0392b", ink: "#ffffff" },
  purple: { fill: "#7d21c7", ink: "#ffffff" },
  grey: { fill: "var(--ele-hairline-strong)", ink: "var(--ele-body)" },
};

const TONE_TEXT_CLASS: Record<PillTone, string> = {
  green: "text-ele-green",
  blue: "text-ele-blue",
  loler: "text-ele-loler",
  amber: "text-amber-400",
  red: "text-ele-danger",
  purple: "text-ele-purple-ink",
  grey: "text-ele-body",
};

/** The text colour class for a coloured WORD with no pill (e.g. an SLA percentage). */
export function toneTextClass(tone: PillTone): string {
  return TONE_TEXT_CLASS[tone];
}

interface CommonProps {
  className?: string;
  title?: string;
}
// Session SC9A (one look for every state): a pill may carry ONE icon (lucide-react), shown in place of the dot. The words are ALWAYS shown
// too, so colour and icon are never the only signal. The registry form takes the icon from the entry; the word form takes an optional `icon`;
// the state form takes a StatePillSpec from primaryStatePill / statePill. Existing callers are unchanged.
type StatusPillProps =
  | (CommonProps & { word: string; tone: PillTone; variant?: PillVariant; icon?: LucideIcon })
  | (CommonProps & { domain: StatusDomain; status: string | null | undefined; variant?: PillVariant })
  | (CommonProps & { state: StatePillSpec; variant?: PillVariant });

export function StatusPill(props: StatusPillProps) {
  const { className = "", title } = props;
  let word: string;
  let tone: PillTone;
  let variant: PillVariant = "tint";
  let Icon: LucideIcon | undefined;
  const dataAttrs: Record<string, string | boolean> = {};

  if ("state" in props) {
    word = props.state.word;
    tone = props.state.tone;
    Icon = props.state.icon;
    variant = props.variant ?? "tint";
    dataAttrs["data-state"] = props.state.kind;
  } else if ("domain" in props) {
    const entry = statusEntry(props.domain, props.status);
    word = entry.label;
    tone = entry.tone;
    variant = props.variant ?? entry.variant ?? "tint";
    Icon = entry.icon;
    dataAttrs["data-status"] = props.status == null ? "" : String(props.status);
    if (!isKnownStatus(props.domain, props.status)) dataAttrs["data-status-unknown"] = "";
  } else {
    word = props.word;
    tone = props.tone;
    variant = props.variant ?? "tint";
    Icon = props.icon;
  }

  const t = TONE[tone];
  const base =
    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-menu text-[0.65rem] uppercase tracking-wide";

  let style: CSSProperties | undefined;
  let showDot = true;
  if (variant === "solid") {
    const s = PILL_SOLID[tone];
    style = { backgroundColor: s.fill, borderColor: s.fill, color: s.ink };
    showDot = false;
  } else if (variant === "outline") {
    style = { backgroundColor: "transparent", borderColor: t.dot, color: t.ink };
  } else {
    style = { borderColor: t.border, color: t.ink, backgroundColor: t.tint };
  }

  return (
    <span title={title} style={style} className={`${base} ${className}`.trim()} {...dataAttrs}>
      {Icon && <Icon aria-hidden data-testid="status-icon" className="h-3 w-3 flex-none" strokeWidth={2.25} />}
      {showDot && !Icon && (
        <span
          aria-hidden
          data-testid="status-dot"
          className="h-1.5 w-1.5 flex-none rounded-full"
          style={{ backgroundColor: t.dot }}
        />
      )}
      {word}
    </span>
  );
}

/**
 * A job, call-out or visit row's ONE status pill (Session SC9A): the most urgent state that applies (entrapment, overdue, on hold, SLA held,
 * follow-on works, awaiting approval; the order is written once in statusRegistry.ts), else the ordinary job status word. Never two.
 * `status` is the job status; every fact is optional.
 */
export function JobStatePill({ facts, className, variant, jobId }: { facts: StateFacts; className?: string; variant?: PillVariant; jobId?: string | null }) {
  const spec = primaryStatePill(facts);
  // Follow-on works links to the faults raised from that visit, in the To fix queue (a router is present wherever a job id is given).
  if (spec && spec.kind === "follow_on" && jobId) {
    return (
      <Link to={followOnWorksHref(jobId)} className="inline-flex rounded-full no-underline" aria-label={`${spec.word}: see the faults raised from this visit`}>
        <StatusPill state={spec} className={className} variant={variant} />
      </Link>
    );
  }
  if (spec) return <StatusPill state={spec} className={className} variant={variant} />;
  return <StatusPill domain="job" status={facts.status} className={className} variant={variant} />;
}
